import React from "react";
import { Link } from "react-router-dom";
import { Mail, Sparkles } from "lucide-react";

export const SUPPORT_EMAIL = "info@astria.fyi";

export const legalLinks = [
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Refund & Cancellation Policy", to: "/refund-policy" },
];

/**
 * Professional site footer with the legal links and support contact
 * Stripe requires to be reachable from the checkout/pricing page.
 */
const SiteFooter: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-black/10 bg-[#F0EAE4] text-[#1B1511]">
      <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-8">
        <div className="grid gap-10 md:grid-cols-3">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span className="font-['Fraunces'] text-xl font-semibold tracking-tight">
                Astria
              </span>
            </div>
            <p className="mt-3 max-w-xs font-['Manrope'] text-sm leading-relaxed text-black/60">
              Guidance for your journey toward clarity and purpose. Upgrade,
              pause, or cancel your plan at any time.
            </p>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-['Manrope'] text-xs font-semibold uppercase tracking-[0.2em] text-black/50">
              Legal
            </h3>
            <ul className="mt-4 space-y-3 font-['Manrope'] text-sm">
              {legalLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-black/70 underline-offset-4 transition hover:text-[#1B1511] hover:underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-['Manrope'] text-xs font-semibold uppercase tracking-[0.2em] text-black/50">
              Contact
            </h3>
            <p className="mt-4 font-['Manrope'] text-sm text-black/60">
              Questions about billing, refunds, or your subscription? We
              typically reply within 2 business days.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-black/15 bg-white/70 px-4 py-2 font-['Manrope'] text-sm font-medium text-[#1B1511] transition hover:border-black/35 hover:bg-white"
            >
              <Mail className="h-4 w-4" />
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-black/10 pt-6 font-['Manrope'] text-xs text-black/50 sm:flex-row sm:items-center sm:justify-between">
          <p>&copy; {year} Astria. All rights reserved.</p>
          <p>Payments are securely processed by Stripe.</p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
